/**
 * @file Sanitize HTML with DOMPurify, then assign innerHTML.
 * Drop-in replacement for v-html that neutralizes XSS from user content.
 *
 * Usage:
 *   <div v-safe-html="htmlString" />        // keep safe tags, kill scripts/handlers
 *   <div v-safe-html.strip="userValue" />   // strip ALL tags, render text only
 *   sanitizeHtml(htmlString)                // function form, for non-template build sites
 */
import createDOMPurify from 'dompurify'
import { type DirectiveBinding, type ObjectDirective } from 'vue'

// Isolated instance -> our hook stays local, never touches the shared DOMPurify
// singleton other modules import (e.g. SurfaceAiMarkdown).
const purifier = createDOMPurify(window)

// DOMPurify 3.3.2 default drops `target` -> add it back so new-tab links keep working.
// Custom <pdlt-mention> is forbidden unless listed; its non-data attributes are stripped too.
// ADD_ATTR is global, not per-tag — mention attrs must be confined to pdlt-mention in the hook.
// `type` is already in DOMPurify's default allowlist; listing it here is a no-op.
// KaTeX MathML wraps the tree in <semantics>/<annotation>, which are not in DOMPurify's MathML
// allowlist — KEEP_CONTENT would flatten the TeX source into <math> for screen readers.
const MENTION_ATTRS = ['mentioner-id', 'mentionee-id', 'mentionee-username']

const KEEP_OPTS = {
  ADD_TAGS: ['pdlt-mention', 'semantics', 'annotation'],
  ADD_ATTR: ['target', ...MENTION_ATTRS],
}
const STRIP_OPTS = { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }

// `target` (named or _blank) without rel -> reverse-tabnabbing: opened page keeps
// window.opener access. Force noopener/noreferrer on every targeted element.
// Preserve existing tokens (autoLink sets `noopener nofollow ugc`) instead of
// replacing the attribute. _blank's browser-implicit noopener doesn't cover
// named targets, so don't rely on it.
purifier.addHook('afterSanitizeAttributes', (node) => {
  // ADD_ATTR is global, not per-tag -> confine the mention attributes to <pdlt-mention>.
  if (node.tagName?.toLowerCase() !== 'pdlt-mention') {
    for (const attr of MENTION_ATTRS) node.removeAttribute(attr)
  }
  if (!node.hasAttribute('target')) return
  const tokens = (node.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean)
  const seen = new Set(tokens)
  for (const required of ['noopener', 'noreferrer']) {
    if (!seen.has(required)) {
      tokens.push(required)
      seen.add(required)
    }
  }
  node.setAttribute('rel', tokens.join(' '))
})

// Single sanitize policy, reused by the directive AND non-template build sites.
const sanitizeHtml = (html: string | null | undefined): string => purifier.sanitize(html ?? '', KEEP_OPTS)

// Templates can bind any expression -> coerce to string before sanitizing.
// Null/empty -> clear innerHTML, skip the DOMPurify call.
const render = (el: HTMLElement, binding: DirectiveBinding<unknown>): void => {
  if (binding.value == null || binding.value === '') {
    el.innerHTML = ''
    return
  }
  const dirty = String(binding.value)
  el.innerHTML = binding.modifiers.strip ? purifier.sanitize(dirty, STRIP_OPTS) : sanitizeHtml(dirty)
}

const vSafeHtml: ObjectDirective<HTMLElement, unknown> = {
  mounted: render,
  // Only re-sanitize when the bound value actually changed.
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      render(el, binding)
    }
  },
}

export { sanitizeHtml, vSafeHtml }
